// PATH: SaberpDeals/frontend/src/components/Modal.tsx
import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

// A dialog that does NOT close when you click the dark backdrop — only via the X,
// a Cancel button inside, or the Escape key. This prevents the "I clicked and the
// form vanished" problem where a stray click on the overlay discarded input.
export default function Modal({
    title, onClose, children, maxWidth = 'max-w-md',
}: {
    title: string;
    onClose: () => void;
    children: ReactNode;
    maxWidth?: string;
}) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4">
            {/* No onClick on the backdrop — clicking outside won't close the form. */}
            <div className={`bg-card rounded-2xl shadow-lg p-6 w-full ${maxWidth}`}>
                <div className="flex items-start justify-between mb-4">
                    <h3 className="font-disp text-lg font-semibold">{title}</h3>
                    <button onClick={onClose} className="text-ink-3 hover:text-ink -mt-1 -mr-1 p-1" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

// Shared field styles for consistency across forms.
export const fieldLabel = 'block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5';
export const fieldInput = 'w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 outline-none focus:border-forest-2 focus:ring-2 focus:ring-forest-soft';
